const AXIS_LABELS = {
  warmth: 'Warmth',
  futurity: 'Futurity',
  chaos: 'Chaos',
  sacredness: 'Sacredness',
  organicness: 'Organicness',
  nostalgia: 'Nostalgia',
  surrealism: 'Surrealism',
  isolation: 'Isolation',
  emotionalSaturation: 'Emotional Saturation',
  corporateDensity: 'Corporate Density'
}

const AXIS_MOTIFS = {
  warmth: ['hearth rituals', 'kinship corridors', 'sheltering light'],
  futurity: ['signal towers', 'threshold machines', 'promissory horizons'],
  chaos: ['fracture weather', 'riot currents', 'volatile rites'],
  sacredness: ['altar processions', 'consecrated silence', 'ceremonial gravity'],
  organicness: ['moss ecologies', 'root memory', 'living thresholds'],
  nostalgia: ['afterimage archives', 'forgotten stations', 'recollected rooms'],
  surrealism: ['mirror weather', 'dream apertures', 'impossible corridors'],
  isolation: ['remote watchfires', 'quiet perimeters', 'exile acoustics'],
  emotionalSaturation: ['fever blooms', 'velvet overload', 'choral intensity'],
  corporateDensity: ['glass regimes', 'protocol plazas', 'admin cathedrals']
}

const TENSION_PAIRS = [
  { a: 'warmth', b: 'isolation', template: 'belonging strains against retreat' },
  { a: 'organicness', b: 'corporateDensity', template: 'living instinct pushes against structural pressure' },
  { a: 'nostalgia', b: 'futurity', template: 'memory competes with acceleration' },
  { a: 'sacredness', b: 'chaos', template: 'ritual order trembles under ecstatic volatility' },
  { a: 'warmth', b: 'corporateDensity', template: 'human closeness is disciplined by polished control' },
  { a: 'surrealism', b: 'corporateDensity', template: 'dream logic flickers inside administrative geometry' },
  { a: 'isolation', b: 'emotionalSaturation', template: 'private distance keeps flooding with feeling' }
]

export default class SymbolicInterpretationEngine {
  constructor() {
    this.territories = []
    this.byId = new Map()
    this.nodeById = new Map()
  }

  update({ territories, vibes, genomeEngine, clusterAssignments }) {
    const neighborsById = this.buildNeighborMap(territories, genomeEngine)

    this.territories = territories.map(territory => this.enrichTerritory(
      territory,
      neighborsById.get(territory.id) || [],
      genomeEngine
    ))

    this.byId = new Map(this.territories.map(territory => [territory.id, territory]))
    this.nodeById = this.buildNodeInterpretations(vibes, genomeEngine, clusterAssignments)
  }

  buildNeighborMap(territories, genomeEngine) {
    const neighborMap = new Map(territories.map(territory => [territory.id, []]))

    for (let i = 0; i < territories.length; i += 1) {
      for (let j = i + 1; j < territories.length; j += 1) {
        const left = territories[i]
        const right = territories[j]
        const centerDistance = Math.hypot(left.center.x - right.center.x, left.center.y - right.center.y)
        const resonance = genomeEngine.calculateSimilarity(left.averageAxes, right.averageAxes)
        const proximity = Math.max(0, 1 - centerDistance / 520)
        const score = resonance * 0.62 + proximity * 0.38

        if (score < 0.3) {
          continue
        }

        const leftEntry = this.createNeighborEntry(right, resonance, proximity, centerDistance)
        const rightEntry = this.createNeighborEntry(left, resonance, proximity, centerDistance)

        neighborMap.get(left.id).push(leftEntry)
        neighborMap.get(right.id).push(rightEntry)
      }
    }

    neighborMap.forEach((neighbors, territoryId) => {
      neighborMap.set(
        territoryId,
        neighbors.sort((left, right) => right.score - left.score).slice(0, 3)
      )
    })

    return neighborMap
  }

  createNeighborEntry(territory, resonance, proximity, distance) {
    return {
      id: territory.id,
      title: territory.title,
      dominantMood: territory.dominantMood,
      score: resonance * 0.62 + proximity * 0.38,
      resonance,
      distance,
      relation: this.describeNeighborRelation(resonance, proximity)
    }
  }

  describeNeighborRelation(resonance, proximity) {
    if (resonance > 0.84) {
      return 'mirror frontier'
    }

    if (resonance > 0.74) {
      return 'resonant border'
    }

    if (proximity > 0.55) {
      return 'pressure border'
    }

    return 'distant echo'
  }

  enrichTerritory(territory, strongestNeighborTerritories, genomeEngine) {
    const primaryAxis = territory.dominantAxes[0]
    const secondaryAxis = territory.dominantAxes[1] || territory.dominantAxes[0]
    const tension = this.findRegionalTension(territory)
    const socialCohesionScore = territory.cohesion * 0.72 + territory.density * 0.28
    const memeticStabilityScore = territory.cohesion * 0.6 + (1 - territory.tension) * 0.25 + territory.density * 0.15
    const dominantEmotionalProfile = this.buildDominantProfile(territory)
    const culturalRole = this.buildCulturalRole(primaryAxis, secondaryAxis, territory)
    const symbolicClimate = this.buildSymbolicClimate(primaryAxis, secondaryAxis, territory)
    const symbolicArchetype = this.buildArchetype(primaryAxis, secondaryAxis, territory)
    const dominantSymbolicMotifs = this.collectMotifs(territory)
    const internalSymbolicConflicts = this.buildInternalConflicts(territory, tension)
    const formationReason = this.buildFormationReason(territory, strongestNeighborTerritories)
    const narrativeDescription = this.buildNarrativeDescription({
      territory,
      symbolicArchetype,
      culturalRole,
      tension,
      strongestNeighborTerritories
    })

    return {
      ...territory,
      title: territory.title,
      symbolicArchetype,
      dominantEmotionalProfile,
      culturalRole,
      regionalTension: tension.summary,
      symbolicClimate,
      socialCohesion: {
        score: socialCohesionScore,
        label: this.describeScale(socialCohesionScore, ['Fragmented', 'Held together', 'Densely bonded'])
      },
      memeticStability: {
        score: memeticStabilityScore,
        label: this.describeScale(memeticStabilityScore, ['Volatile', 'Adaptable', 'Persistent'])
      },
      narrativeDescription,
      dominantSymbolicMotifs,
      dominantVibeFamilies: territory.strongestVibes,
      strongestNeighborTerritories,
      internalSymbolicConflicts,
      regionalRoleSummary: `${territory.title} functions as ${culturalRole.toLowerCase()}.`,
      formationReason,
      compatibilityProfile: {
        ...territory.compatibilityProfile,
        neighbors: strongestNeighborTerritories.map(neighbor => `${neighbor.title} forms a ${neighbor.relation}`)
      }
    }
  }

  buildDominantProfile(territory) {
    const dominant = territory.dominantAxes
      .slice(0, 3)
      .map(entry => `${entry.label} ${entry.value.toFixed(0)}`)
      .join(' · ')

    return `${dominant} with ${territory.weakestAxes[0].label.toLowerCase()} suppressed.`
  }

  buildCulturalRole(primaryAxis, secondaryAxis, territory) {
    const highCohesion = territory.cohesion > 0.82
    const tense = territory.tension > 0.24
    const roleMap = {
      warmth: highCohesion ? 'a refuge for relational memory' : 'a shelter negotiating intimacy and drift',
      futurity: tense ? 'a threshold market for unstable futures' : 'a launch corridor for anticipatory culture',
      chaos: tense ? 'an insurgent weather system of unstable symbols' : 'a permissive zone for improvisational culture',
      sacredness: highCohesion ? 'a ritual basin for shared meaning' : 'a contested shrine of symbolic gravity',
      organicness: highCohesion ? 'a living commons of slow-grown affinities' : 'an overgrown border where identities recombine',
      nostalgia: highCohesion ? 'an archive chamber for retained feeling' : 'a mourning district for unfinished memory',
      surrealism: tense ? 'a dream corridor where logics splice together' : 'a mirage conservatory for altered perception',
      isolation: tense ? 'an outer perimeter of guarded inwardness' : 'a quiet observatory for solitary patterning',
      emotionalSaturation: tense ? 'an overbright theater of feeling pressure' : 'a ceremonial chorus of heightened affect',
      corporateDensity: tense ? 'a polished regime enforcing symbolic order' : 'a disciplined plaza for systemized belonging'
    }

    return roleMap[primaryAxis.axis] || `a territory shaped by ${primaryAxis.label.toLowerCase()} and ${secondaryAxis.label.toLowerCase()}`
  }

  buildSymbolicClimate(primaryAxis, secondaryAxis, territory) {
    const tensionTone = territory.tension > 0.26 ? 'charged' : territory.cohesion > 0.82 ? 'settled' : 'shifting'
    return `${tensionTone} ${primaryAxis.label.toLowerCase()} under ${secondaryAxis.label.toLowerCase()} weather`
  }

  buildArchetype(primaryAxis, secondaryAxis, territory) {
    if (territory.cohesion > 0.86) {
      return `${primaryAxis.label} enclave with ${secondaryAxis.label.toLowerCase()} rites`
    }

    if (territory.tension > 0.28) {
      return `${primaryAxis.label} frontier under ${secondaryAxis.label.toLowerCase()} strain`
    }

    return `${primaryAxis.label} province of ${secondaryAxis.label.toLowerCase()} drift`
  }

  collectMotifs(territory) {
    return territory.dominantAxes
      .slice(0, 3)
      .flatMap(entry => AXIS_MOTIFS[entry.axis].slice(0, 1))
      .concat(territory.strongestVibes.slice(0, 1).map(name => `${name.toLowerCase()} echoes`))
      .slice(0, 4)
  }

  findRegionalTension(territory) {
    const axes = territory.averageAxes
    let bestMatch = null
    let bestScore = 0

    TENSION_PAIRS.forEach(pair => {
      const left = axes[pair.a] || 0
      const right = axes[pair.b] || 0
      const score = Math.min(left, right) * (1 + territory.tension)

      if (score > bestScore) {
        bestScore = score
        bestMatch = pair
      }
    })

    if (!bestMatch || bestScore < 28) {
      return {
        summary: territory.tension > 0.24
          ? 'regional meanings remain in soft dispute'
          : 'the territory holds a relatively unified symbolic weather',
        axes: []
      }
    }

    return {
      summary: bestMatch.template,
      axes: [bestMatch.a, bestMatch.b]
    }
  }

  buildInternalConflicts(territory, tension) {
    const conflicts = [tension.summary]

    if (territory.weakestAxes[0]) {
      conflicts.push(`${territory.weakestAxes[0].label} remains underdeveloped inside the region`) 
    }

    if (territory.tension > 0.26) {
      conflicts.push('peripheral members carry alternate symbolic weather back into the center')
    }

    return conflicts.slice(0, 3)
  }

  buildFormationReason(territory, strongestNeighborTerritories) {
    const neighborText = strongestNeighborTerritories[0]
      ? `while negotiating the nearby pull of ${strongestNeighborTerritories[0].title}`
      : 'in relative symbolic isolation'

    return `${territory.title} formed because ${territory.strongestVibes[0]}-like signals clustered around ${territory.dominantAxes[0].label.toLowerCase()} and ${territory.dominantAxes[1].label.toLowerCase()}, ${neighborText}.`
  }

  buildNarrativeDescription({ territory, symbolicArchetype, culturalRole, tension, strongestNeighborTerritories }) {
    const neighborClause = strongestNeighborTerritories[0]
      ? `It leans toward ${strongestNeighborTerritories[0].title} as a ${strongestNeighborTerritories[0].relation}.`
      : 'It currently listens more to its own internal weather than to external regions.'

    return `${territory.title} reads as a ${symbolicArchetype.toLowerCase()}, with ${territory.strongestVibes[0]} and ${territory.strongestVibes[1] || territory.strongestVibes[0]} acting as its most visible carriers. Its cultural role is ${culturalRole}, while ${tension.summary}. ${neighborClause}`
  }

  buildNodeInterpretations(vibes, genomeEngine, clusterAssignments) {
    const interpretations = new Map()

    vibes.forEach(vibe => {
      const territoryId = clusterAssignments.get(vibe.id)
      const territory = territoryId ? this.byId.get(territoryId) : null

      if (!territory) {
        return
      }

      const nearestEmotionalNeighbors = vibes
        .filter(candidate => candidate.id !== vibe.id)
        .map(candidate => ({
          name: candidate.name,
          score: genomeEngine.calculateSimilarity(vibe.axes, candidate.axes)
        }))
        .sort((left, right) => right.score - left.score)
        .slice(0, 3)

      const emotionalContributionScore = genomeEngine.calculateSimilarity(vibe.axes, territory.averageAxes)
      const distanceToCenter = Math.hypot(vibe.x - territory.center.x, vibe.y - territory.center.y)
      const territoryInfluenceStrength = Math.max(0, 1 - distanceToCenter / Math.max(territory.radius, 1)) * 0.48 + emotionalContributionScore * 0.52
      const dominantContributionAxes = territory.dominantAxes
        .filter(entry => (vibe.axes[entry.axis] || 0) >= Math.max(35, entry.value * 0.72))
        .slice(0, 2)
        .map(entry => entry.label.toLowerCase())
      const symbolicRoleInsideTerritory = this.describeNodeRole({
        vibe,
        territory,
        emotionalContributionScore,
        territoryInfluenceStrength,
        distanceToCenter
      })

      interpretations.set(vibe.id, {
        whyNodeBelongs: dominantContributionAxes.length > 0
          ? `${vibe.name} belongs here because it reinforces ${dominantContributionAxes.join(' and ')} already concentrated in ${territory.title}.`
          : `${vibe.name} remains inside ${territory.title} as a softer peripheral echo of the regional climate.`,
        nearestEmotionalNeighbors,
        symbolicRoleInsideTerritory,
        emotionalContributionScore,
        territoryInfluenceStrength,
        dominantContributionAxes
      })
    })

    return interpretations
  }

  describeNodeRole({ territory, emotionalContributionScore, territoryInfluenceStrength, distanceToCenter }) {
    if (emotionalContributionScore > 0.84 && territoryInfluenceStrength > 0.76) {
      return 'regional anchor'
    }

    if (distanceToCenter > territory.radius * 0.72) {
      return 'perimeter scout'
    }

    if (territory.tension > 0.24) {
      return 'boundary mediator'
    }

    if (emotionalContributionScore > 0.76) {
      return 'cohesion carrier'
    }

    return 'atmospheric bridge'
  }

  describeScale(score, labels) {
    if (score < 0.45) {
      return labels[0]
    }

    if (score < 0.72) {
      return labels[1]
    }

    return labels[2]
  }

  getTerritories() {
    return this.territories
  }

  getById(id) {
    return this.byId.get(id) ?? null
  }

  getNodeInterpretation(vibeId) {
    return this.nodeById.get(vibeId) ?? null
  }
}
